import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

interface SmartTableTourState {
  isActive: boolean;
  currentStep: number;
  postTourActivationRequested: boolean;
  start: () => void;
  end: () => void;
  setStep: (step: number) => void;
  requestPostTourActivation: () => void;
  consumePostTourActivationRequest: () => void;
}

const SmartTableTourContext = createContext<SmartTableTourState | null>(null);

export const SmartTableTourProvider = ({ children }: { children: ReactNode }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [postTourActivationRequested, setPostTourActivationRequested] = useState(false);

  const start = useCallback(() => {
    setPostTourActivationRequested(false);
    setIsActive(true);
    setCurrentStep(0);
  }, []);

  const end = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
  }, []);

  const requestPostTourActivation = useCallback(() => {
    setPostTourActivationRequested(true);
  }, []);

  const consumePostTourActivationRequest = useCallback(() => {
    setPostTourActivationRequested(false);
  }, []);

  const value = useMemo<SmartTableTourState>(
    () => ({
      isActive,
      currentStep,
      postTourActivationRequested,
      start,
      end,
      setStep: setCurrentStep,
      requestPostTourActivation,
      consumePostTourActivationRequest,
    }),
    [
      consumePostTourActivationRequest,
      currentStep,
      end,
      isActive,
      postTourActivationRequested,
      requestPostTourActivation,
      start,
    ],
  );

  return <SmartTableTourContext.Provider value={value}>{children}</SmartTableTourContext.Provider>;
};

export const useSmartTableTourContext = (): SmartTableTourState => {
  const ctx = useContext(SmartTableTourContext);
  if (!ctx) {
    throw new Error("useSmartTableTourContext must be used within a SmartTableTourProvider");
  }
  return ctx;
};

export const useOptionalSmartTableTourContext = (): SmartTableTourState | null =>
  useContext(SmartTableTourContext);
